import { useIntl } from "react-intl";

import Button from "@/components/wpl/button/src";
import {
    FeaturesTable,
    FeaturesTableHeader,
    FeaturesTableRow,
    FeaturesTableSection
} from "@/components/wpl/pricing/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import AtlassianGuardStandard from "@/templates/jira-software-pricing-page/src/modal-contents/team24-modals/atlassian-guard-standard.tsx";
import UnlimitedGoals from "@/templates/jira-software-pricing-page/src/modal-contents/team24-modals/unlimited-goals.tsx";

import AdminInsights from "../modal-contents/admin-insights";
import AppsAndIntegrations from "../modal-contents/apps-and-integrations";
import AtlassianAnalytics from "../modal-contents/atlassian-anayltics";
import AtlassianDataLake from "../modal-contents/atlassian-data-lake";
import AtlassianIntelligence from "../modal-contents/atlassian-intelligence";
import AuditLogs from "../modal-contents/audit-logs";
import AuditLogsForUserAcitivity from "../modal-contents/audit-logs-for-user-activity";
import Automation from "../modal-contents/automation";
import BusinessContinuityAndDisasterRecovery from "../modal-contents/business-continuity-and-disaster-recovery";
import BYOK from "../modal-contents/byok";
import CentralizedUserSubscriptions from "../modal-contents/centralized-user-subscriptions";
import CustomizableWorkflows from "../modal-contents/customizable-workflows";
import DataConnectors from "../modal-contents/data-connectors";
import DataResidency from "../modal-contents/data-residency";
import DomainVerificationAndAccountCapture from "../modal-contents/domain-verification-and-account-capture";
import EmailNotifications from "../modal-contents/email-notifications";
import EncryptionInTransitAndAtRest from "../modal-contents/encryption-in-transit-and-rest";
import IPAllowlisting from "../modal-contents/ip-allowlisting";
import MobileDeviceManagment from "../modal-contents/mobile-device-management";
import MultipleIdP from "../modal-contents/multiple-idp";
import NumberOfSites from "../modal-contents/number-of-sites";
import PasswordPolicies from "../modal-contents/password-policies";
import ProductRequests from "../modal-contents/product-requests";
import ProjectArchiving from "../modal-contents/project-archiving";
import ReleaseTracks from "../modal-contents/release-tracks";
import Sandbox from "../modal-contents/sandbox";
import SessionDurationManagement from "../modal-contents/session-duration-management";
import AdvancedPlanning from "../modal-contents/spork-modals/advanced-planning-plans";
import AnonymousAccessSpork from "../modal-contents/spork-modals/anonymous-access-spork";
import Approvals from "../modal-contents/spork-modals/approvals";
import BacklogSpork from "../modal-contents/spork-modals/backlog-spork";
import BoardView from "../modal-contents/spork-modals/board-view";
import CalendarView from "../modal-contents/spork-modals/calendar-view";
import CapacityManagementSpork from "../modal-contents/spork-modals/capacity-management-spork";
import Dashboards from "../modal-contents/spork-modals/dashboards";
import DependencyManagementSpork from "../modal-contents/spork-modals/dependency-management-spork";
import ExpandableIssueHierarchy from "../modal-contents/spork-modals/expandable-issue-hierarchy";
import IssueLevelSecurity from "../modal-contents/spork-modals/issue-level-security";
import ListView from "../modal-contents/spork-modals/list-view";
import ProjectTemplates from "../modal-contents/spork-modals/project-templates";
import ReportingAndInsightsSpork from "../modal-contents/spork-modals/reporting-and-insights-spork";
import ScenarioModeling from "../modal-contents/spork-modals/scenario-modeling";
import SummaryView from "../modal-contents/spork-modals/summary-view";
import TimelineView from "../modal-contents/spork-modals/timeline-view";
import UnlimitedForms from "../modal-contents/spork-modals/unlimited-forms";
import UnlimitedIssues from "../modal-contents/spork-modals/unlimited-issues";
import UnlimitedProjects from "../modal-contents/spork-modals/unlimited-projects";
import UserRolesPermissionsSpork from "../modal-contents/spork-modals/user-roles-permissions-spork";
import Storage from "../modal-contents/storage";
import EmployeeDirectory from "../modal-contents/team24-modals/employee-directory";
import GoalsProjectsCommunication from "../modal-contents/team24-modals/goals-projects-communication";
import UserCounts from "../modal-contents/user-counts";
import UserLimitPerSite from "../modal-contents/user-limit-per-site";

const tiers = ["Free", "Standard", "Premium", "Enterprise"];

const SporkCloudFeaturesTables = () => {
    const intl = useIntl();

    const buttonText = intl.formatMessage({
        id: "JiraSoftwarePricingPageTemplate.features.learnAboutFeatures",
        defaultMessage: "Learn about features"
    });

    return (
        <FeaturesTableSection>
            <FeaturesTableHeader>
                {intl.formatMessage({
                    id: "JiraSoftwarePricingPageTemplate.featuresTableHeader",
                    defaultMessage: "Compare features"
                })}
            </FeaturesTableHeader>
            <ProjectManagementEssentials />
            <BringAllYourTeamsIntoJira />
            <SimplifyCrossFunctionalWork />
            <ScaleOnAtlassianPlatform />
            <ManageUsersAndProducts />
            <SecurityAndCompliance />
            <CustomerSupport />
            <Button
                type="secondary"
                color="darkblue"
                text={buttonText}
                aria-label={buttonText}
                url={useLocalizedHref("/software/jira/features")}
                dataEventProps={{
                    "data-event": "clicked",
                    "data-product-key": "jiraSoftware",
                    "data-event-component": "linkButton",
                    "data-event-container": "featuresSection"
                }}
            />
        </FeaturesTableSection>
    );
};

const ProjectManagementEssentials = () => {
    const intl = useIntl();

    const messages = {
        caption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.projectManagementEssentialsCaption",
            defaultMessage: "Project management essentials"
        }),
        subCaption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.projectManagementEssentialsSubCaption",
            defaultMessage:
                "Everything teams need to get started. Always free, no use limits and no fuss."
        }),
        unlimitedGoals: intl.formatMessage({
            id: "JiraSoftwarePricingPageTeam24.features.unlimitedGoals.title",
            defaultMessage: "Unlimited goals"
        }),
        unlimitedIssues: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.unlimitedIssues",
            defaultMessage: "Unlimited Issues"
        }),
        unlimitedProjects: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.unlimitedProjects",
            defaultMessage: "Unlimited projects"
        }),
        unlimitedForms: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.unlimitedForms",
            defaultMessage: "Unlimited forms"
        }),
        customizableWorkflows: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.customizableWorkflows",
            defaultMessage: "Customizable workflows"
        }),
        projectTemplates: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.projectTemplates",
            defaultMessage: "Project templates"
        }),
        backlogView: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.backlogView",
            defaultMessage: "Backlog view"
        }),
        listView: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.listView",
            defaultMessage: "List view"
        }),
        boardView: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.boardView",
            defaultMessage: "Board view"
        }),
        timelineView: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.timelineView",
            defaultMessage: "Timeline view"
        }),
        calendarView: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.calendarView",
            defaultMessage: "Calendar view"
        }),
        summaryView: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.automation.summaryView",
            defaultMessage: "Summary view"
        }),
        dashboards: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.dashboards",
            defaultMessage: "Dashboards"
        }),
        reportsAndTeamInsights: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.reportsAndTeamInsights",
            defaultMessage: "Reports and team insights"
        }),
        appsAndIntegrations: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.appsAndIntegrations",
            defaultMessage: "Apps and integrations"
        }),
        iosAndAndroidApps: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.iosAndAndroidApps",
            defaultMessage: "iOS and Android apps"
        }),
        beta: intl.formatMessage({
            id: "ConfluencePricingPage.features.beta",
            defaultMessage: "Beta"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.caption}
            subCaptionText={messages.subCaption}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.unlimitedGoals,
                    content: UnlimitedGoals
                }}
                lozengeText={messages.beta}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.unlimitedIssues,
                    content: UnlimitedIssues
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.unlimitedProjects,
                    content: UnlimitedProjects
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.unlimitedForms,
                    content: UnlimitedForms
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.customizableWorkflows,
                    content: CustomizableWorkflows
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.projectTemplates,
                    content: ProjectTemplates
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.backlogView,
                    content: BacklogSpork
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.listView,
                    content: ListView
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.boardView,
                    content: BoardView
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.timelineView,
                    content: TimelineView
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.calendarView,
                    content: CalendarView
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.summaryView,
                    content: SummaryView
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dashboards,
                    content: Dashboards
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.reportsAndTeamInsights,
                    content: ReportingAndInsightsSpork
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.appsAndIntegrations,
                    content: AppsAndIntegrations
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    type: "external",
                    href: useLocalizedHref("/software/jira/mobile-app"),
                    text: messages.iosAndAndroidApps
                }}
                tierCellsContent={[true, true, true, true]}
            />
        </FeaturesTable>
    );
};

const BringAllYourTeamsIntoJira = () => {
    const intl = useIntl();

    const messages = {
        caption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.bringAllYourTeamsIntoJiraCaption",
            defaultMessage: "Bring all your teams into Jira"
        }),
        subCaption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.bringAllYourTeamsIntoJiraSubCaption",
            defaultMessage:
                "Give your teams space to grow with unlimited users, more storage, and flexible permissions."
        }),
        userLimit: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.userLimit",
            defaultMessage: "User limit per site"
        }),
        users10: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.userLimit.10",
            defaultMessage: "10 users"
        }),
        users50k: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.userLimit.50k",
            defaultMessage: "50,000 users"
        }),
        numberOfSites: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.numberOfSites",
            defaultMessage: "Number of sites"
        }),
        one: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.bringAllYourTeamsIntoJira.numberOfSites.one",
            defaultMessage: "1 site"
        }),
        multiple: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.bringAllYourTeamsIntoJira.numberOfSites.multiple",
            defaultMessage: "Up to 150"
        }),
        storage: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.storage",
            defaultMessage: "Storage"
        }),
        fileStorage2GB: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.fileStorage2GB",
            defaultMessage: "2 GB file storage"
        }),
        fileStorage250GB: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.fileStorage250GB",
            defaultMessage: "250 GB file storage"
        }),
        fileStorageUnlimited: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.fileStorageUnlimited",
            defaultMessage: "Unlimited file storage"
        }),
        emailNotifications: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.emailNotifications",
            defaultMessage: "Email notifications"
        }),
        hundredEmails: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.hundredEmails",
            defaultMessage: "100 emails per day"
        }),
        unlimitedEmails: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.unlimitedEmails",
            defaultMessage: "Unlimited"
        }),
        userRolesAndPermissions: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.userRolesAndPermissions",
            defaultMessage: "User roles and permissions"
        }),
        anonymousAccess: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.anonymousAccess",
            defaultMessage: "Anonymous access"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.caption}
            subCaptionText={messages.subCaption}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.userLimit,
                    content: UserLimitPerSite
                }}
                tierCellsContent={[
                    messages.users10,
                    messages.users50k,
                    messages.users50k,
                    messages.users50k
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.numberOfSites,
                    content: NumberOfSites
                }}
                tierCellsContent={[
                    messages.one,
                    messages.one,
                    messages.one,
                    messages.multiple
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.storage,
                    content: Storage
                }}
                tierCellsContent={[
                    messages.fileStorage2GB,
                    messages.fileStorage250GB,
                    messages.fileStorageUnlimited,
                    messages.fileStorageUnlimited
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.emailNotifications,
                    content: EmailNotifications
                }}
                tierCellsContent={[
                    messages.hundredEmails,
                    messages.unlimitedEmails,
                    messages.unlimitedEmails,
                    messages.unlimitedEmails
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.userRolesAndPermissions,
                    content: UserRolesPermissionsSpork
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.anonymousAccess,
                    content: AnonymousAccessSpork
                }}
                tierCellsContent={[false, true, true, true]}
            />
        </FeaturesTable>
    );
};

const SimplifyCrossFunctionalWork = () => {
    const intl = useIntl();

    const messages = {
        caption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.simplifyCrossFunctionalWorkCaption",
            defaultMessage: "Simplify cross-functional work"
        }),
        subCaption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.simplifyCrossFunctionalWorkSubCaption",
            defaultMessage:
                "Break down complex goals and projects with cross-team planning and tracking tools."
        }),
        advancedPlanning: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.advancedPlanning",
            defaultMessage: "Advanced planning (Plans)"
        }),
        dependencyManagement: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.dependencyManagementSpork",
            defaultMessage: "Dependency management"
        }),
        singleProject: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.singleProject",
            defaultMessage: "Single Project"
        }),
        crossProject: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.crossProjectSpork",
            defaultMessage: "Cross-project"
        }),
        capacityManagement: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.capacityManagementSpork",
            defaultMessage: "Capacity management"
        }),
        expandableIssueHierarchy: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.expandableIssueHierarchy",
            defaultMessage: "Expandable Issue Hierarchy"
        }),
        scenarioModeling: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.scenarioModeling",
            defaultMessage: "Scenario Modeling"
        }),
        approvals: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.approvals",
            defaultMessage: "Approvals"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.caption}
            subCaptionText={messages.subCaption}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.advancedPlanning,
                    content: AdvancedPlanning
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dependencyManagement,
                    content: DependencyManagementSpork
                }}
                tierCellsContent={[
                    messages.singleProject,
                    messages.singleProject,
                    messages.crossProject,
                    messages.crossProject
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.capacityManagement,
                    content: CapacityManagementSpork
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.expandableIssueHierarchy,
                    content: ExpandableIssueHierarchy
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.scenarioModeling,
                    content: ScenarioModeling
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.approvals,
                    content: Approvals
                }}
                tierCellsContent={[false, false, true, true]}
            />
        </FeaturesTable>
    );
};

const ScaleOnAtlassianPlatform = () => {
    const intl = useIntl();

    const messages = {
        caption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.scaleOnAtlassianPlatformCaption",
            defaultMessage: "Scale on the Atlassian platform"
        }),
        subCaption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.scaleOnAtlassianPlatformSubCaption",
            defaultMessage:
                "Move fast while staying aligned by connecting your teams, goals, and data on a secure platform."
        }),
        atlassianIntelligence: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.atlassianIntelligence",
            defaultMessage: "Atlassian Intelligence (AI)"
        }),
        goalsProjectsCommunication: intl.formatMessage({
            id: "JiraSoftwarePricingPageTeam24.features.goalsProjectsCommunication.title",
            defaultMessage: "Goals, projects, and communication"
        }),
        employeeDirectory: intl.formatMessage({
            id: "JiraSoftwarePricingPageTeam24.features.employeeDirectory.title",
            defaultMessage: "Employee directory for people and teams"
        }),
        automation: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.automation",
            defaultMessage: "Automation"
        }),
        ruleRuns100: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.automation.100",
            defaultMessage: "100 rule runs per month"
        }),
        ruleRuns1700: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.automation-spork.1700",
            defaultMessage: "1,700 rule runs per month"
        }),
        ruleRuns1000: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.automation-spork.1000",
            defaultMessage: "1,000 rule runs per user per month"
        }),
        ruleRunsUnlimited: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.automation.unlimited",
            defaultMessage: "Unlimited"
        }),
        atlassianAnalytics: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.atlassianAnalytics",
            defaultMessage: "Atlassian Analytics"
        }),
        atlassianDataLake: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.atlassianDataLake",
            defaultMessage: "Atlassian Data Lake"
        }),
        dataConnectors: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.dataConnectors",
            defaultMessage: "Data connectors"
        }),
        atlassianAccess: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.atlassianAccess",
            defaultMessage:
                "Atlassian Access (SSO, SCIM, Active Directory Sync)"
        }),
        requiresAccessSubscription: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.requiresAccessSubscription",
            defaultMessage: "Requires Access subscription"
        }),
        included: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.included",
            defaultMessage: "Included"
        }),
        new: intl.formatMessage({
            id: "JiraSoftwarePricingPage.priceFinder.newTagText",
            defaultMessage: "New"
        }),
        beta: intl.formatMessage({
            id: "ConfluencePricingPage.features.beta",
            defaultMessage: "Beta"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.caption}
            subCaptionText={messages.subCaption}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianIntelligence,
                    content: AtlassianIntelligence
                }}
                lozengeText={messages.new}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.goalsProjectsCommunication,
                    content: GoalsProjectsCommunication
                }}
                lozengeText={messages.beta}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.employeeDirectory,
                    content: EmployeeDirectory
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.automation,
                    content: Automation
                }}
                tierCellsContent={[
                    messages.ruleRuns100,
                    messages.ruleRuns1700,
                    messages.ruleRuns1000,
                    messages.ruleRunsUnlimited
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianAnalytics,
                    content: AtlassianAnalytics
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianDataLake,
                    content: AtlassianDataLake
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dataConnectors,
                    content: DataConnectors
                }}
                tierCellsContent={[false, false, false, true]}
            />
        </FeaturesTable>
    );
};

const ManageUsersAndProducts = () => {
    const intl = useIntl();

    const messages = {
        caption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.manageUsersAndProductsCaption",
            defaultMessage: "Manage users & products"
        }),
        subCaption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.manageUsersAndProductsSubCaption",
            defaultMessage:
                "Safely administer products and apps across your entire organization."
        }),
        domainVerificationAndAccountCapture: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.domainVerificationAndAccountCapture",
            defaultMessage: "Domain verification and account capture"
        }),
        sessionDurationManagement: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.sessionDurationManagement",
            defaultMessage: "Session duration management (desktop)"
        }),
        adminInsights: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.adminInsights",
            defaultMessage: "Admin insights"
        }),
        sandbox: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.sandbox",
            defaultMessage: "Sandbox"
        }),
        releaseTracks: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.releaseTracks",
            defaultMessage: "Release tracks"
        }),
        projectArchiving: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.projectArchiving",
            defaultMessage: "Project archiving"
        }),
        userCounts: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.userCounts",
            defaultMessage: "User counts"
        }),
        centralizedUserSubscriptions: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.centralizedUserSubscriptions",
            defaultMessage: "Centralized user subscriptions"
        }),
        productRequests: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.productRequests",
            defaultMessage: "Product requests"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.caption}
            subCaptionText={messages.subCaption}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.domainVerificationAndAccountCapture,
                    content: DomainVerificationAndAccountCapture
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.sessionDurationManagement,
                    content: SessionDurationManagement
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.adminInsights,
                    content: AdminInsights
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.sandbox,
                    content: Sandbox
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.releaseTracks,
                    content: ReleaseTracks
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.projectArchiving,
                    content: ProjectArchiving
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.userCounts,
                    content: UserCounts
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.centralizedUserSubscriptions,
                    content: CentralizedUserSubscriptions
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.productRequests,
                    content: ProductRequests
                }}
                tierCellsContent={[false, false, false, true]}
            />
        </FeaturesTable>
    );
};

const SecurityAndCompliance = () => {
    const intl = useIntl();

    const messages = {
        caption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.securityAndComplianceCaption",
            defaultMessage: "Security & Compliance"
        }),
        subCaption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.securityAndComplianceSubCaption",
            defaultMessage:
                "Secure your users, products, and data on Atlassian Cloud."
        }),
        atlassianGuardStandard: intl.formatMessage({
            id: "JiraSoftwarePricingPageTeam24.features.atlassianGuardStandard.title",
            defaultMessage:
                "Atlassian Guard Standard (SSO, SCIM, Active Directory Sync)"
        }),
        requiresAtlassianGuardStandardSubscription: intl.formatMessage({
            id: "JiraSoftwarePricingPageTeam24.features.requiresAtlassianGuardStandardSubscription",
            defaultMessage: "Requires Atlassian Guard Standard subscription"
        }),
        included: intl.formatMessage({
            id: "JiraSoftwarePricingPageTeam24.features.included",
            defaultMessage: "Included"
        }),
        passwordPolicies: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.passwordPolicies",
            defaultMessage: "Password policies"
        }),
        encryptionInTransitAndAtRest: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.encryptionInTransitAndAtRest",
            defaultMessage: "Encryption in transit and at rest"
        }),
        businessContinuityAndDisasterRecovery: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.businessContinuityAndDisasterRecovery",
            defaultMessage: "Business continuity and disaster recovery"
        }),
        mdm: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.mdm",
            defaultMessage: "MDM (Mobile Device Management)"
        }),
        auditLogsSiteChanges: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.auditLogsSiteChanges",
            defaultMessage: "Audit logs - site changes"
        }),
        dataResidency: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.dataResidency",
            defaultMessage: "Data residency"
        }),
        issueLevelSecurity: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.issueLevelSecurity",
            defaultMessage: "Issue-level security"
        }),
        ipAllowlisting: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.ipAllowlisting",
            defaultMessage: "IP allowlisting"
        }),
        multipleIdP: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.multipleIdPSpork",
            defaultMessage: "Multiple IdP"
        }),
        auditLogsUserActivitySpork: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.auditLogsUserActivitySpork",
            defaultMessage: "Audit logs - user activity"
        }),
        byok: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.byokSpork",
            defaultMessage: "BYOK"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.caption}
            subCaptionText={messages.subCaption}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianGuardStandard,
                    content: AtlassianGuardStandard
                }}
                tierCellsContent={[
                    messages.requiresAtlassianGuardStandardSubscription,
                    messages.requiresAtlassianGuardStandardSubscription,
                    messages.requiresAtlassianGuardStandardSubscription,
                    messages.included
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.passwordPolicies,
                    content: PasswordPolicies
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.encryptionInTransitAndAtRest,
                    content: EncryptionInTransitAndAtRest
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.businessContinuityAndDisasterRecovery,
                    content: BusinessContinuityAndDisasterRecovery
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.mdm,
                    content: MobileDeviceManagment
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.auditLogsSiteChanges,
                    content: AuditLogs
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dataResidency,
                    content: DataResidency
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.issueLevelSecurity,
                    content: IssueLevelSecurity
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.ipAllowlisting,
                    content: IPAllowlisting
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.multipleIdP,
                    content: MultipleIdP
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.auditLogsUserActivitySpork,
                    content: AuditLogsForUserAcitivity
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{ text: messages.byok, content: BYOK }}
                tierCellsContent={[false, false, false, true]}
            />
        </FeaturesTable>
    );
};

const CustomerSupport = () => {
    const intl = useIntl();

    const messages = {
        caption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.customerSupportCaption",
            defaultMessage: "Customer Support"
        }),
        subCaption: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.customerSupportSubCaption",
            defaultMessage:
                "Get help when you need it from experts in Atlassian Community or our Cloud support team."
        }),
        support: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.support",
            defaultMessage: "Support"
        }),
        communitySupport: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.communitySupport",
            defaultMessage: "Community Support"
        }),
        localBusinessHours: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.localBusinessHours",
            defaultMessage: "Local Business Hours"
        }),
        premiumSupport: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.premiumSupport",
            defaultMessage: "24/7 Premium Support"
        }),
        enterpriseSupport: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.enterpriseSupport",
            defaultMessage: "24/7 Enterprise Support"
        }),
        guaranteedUptimeSLA: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.guaranteedUptimeSLA",
            defaultMessage: "Guaranteed uptime SLA"
        }),
        percent999: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.percent999",
            defaultMessage: "99.9%"
        }),
        percent9995: intl.formatMessage({
            id: "JiraSoftwarePricingPageTemplate.features.percent9995",
            defaultMessage: "99.95%"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.caption}
            subCaptionText={messages.subCaption}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    type: "external",
                    href: useLocalizedHref("/enterprise/support-services"),
                    text: messages.support
                }}
                tierCellsContent={[
                    messages.communitySupport,
                    messages.localBusinessHours,
                    messages.premiumSupport,
                    messages.enterpriseSupport
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    type: "external",
                    href: useLocalizedHref("/legal/sla"),
                    text: messages.guaranteedUptimeSLA
                }}
                tierCellsContent={[
                    false,
                    false,
                    messages.percent999,
                    messages.percent9995
                ]}
            />
        </FeaturesTable>
    );
};

export default SporkCloudFeaturesTables;
